import {gapFromDensity} from '@github-ui/action-bar'
import {testIdProps} from '@github-ui/test-id-props'
import {Box, Heading, merge, type SxProp} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type {ReactElement, ReactNode, Suspense} from 'react'
import {isValidElement, useCallback, useEffect, useRef} from 'react'

import {announceFromElement} from '@github-ui/aria-live'
import {useNextHeaderTag} from '../hooks/use-next-header-tag'
import {useListViewId} from './IdContext'
import {useListViewSelection} from './SelectionContext'
import {useListViewTitle} from './TitleContext'

import type {ListViewSectionFilterLinks} from './SectionFilterLink'
import {ListViewActionBar, type ListViewActionBarProps} from './ActionBar'
import {ListViewSectionFilters} from './SectionFilters'
import {ListViewSelectAllCheckbox} from './SelectAllCheckbox'

export type ListViewMetadataProps = {
  /**
   * The rendered title of the list or information regarding its contents.
   */
  title?: string | ReactNode
  /**
   * Links for applying any categorical, mutually exclusive section filters for the content of the ListView.
   */
  sectionFilters?: ListViewSectionFilterLinks | ReactElement<typeof Suspense>
  /**
   * Used for assistive announcements that are reflective of the visual updates to the list container a sighted user
   * will experience when using the list. Defaults to no announcement. Visually hidden, for screenreaders only.
   */
  assistiveAnnouncement?: string
  /**
   * Callback for when the checkbox for selecting and deselecting all list items is toggled.
   */
  onToggleSelectAll?: (isSelectAllChecked: boolean) => void
  /**
   * Styling for the actions container.
   */
  actionsSx?: SxProp['sx']
  /**
   * Optional additional actions to show. Will not fall into an overflow dropdown menu to accommodate small screen
   * widths.
   */
  children?: ReactNode
} & SxProp &
  ListViewActionBarProps

const headerStyles: BetterSystemStyleObject = {
  display: 'flex',
  px: 2,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontSize: 1,
}

const defaultStyles: BetterSystemStyleObject = {
  p: 2,
  backgroundColor: 'canvas.subtle',
  borderTopRightRadius: 2,
  borderTopLeftRadius: 2,
  height: 48,
  borderBottom: '1px solid',
  borderBottomColor: 'border.muted',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
}

export const ListViewMetadata = ({
  title,
  assistiveAnnouncement,
  sectionFilters,
  children,
  sx,
  actionsSx,
  onToggleSelectAll,
  ...actionBarProps
}: ListViewMetadataProps) => {
  const {idPrefix} = useListViewId()
  const {anyItemsSelected} = useListViewSelection()
  const {setHasMetadataTitle} = useListViewTitle()
  const titleTag = useNextHeaderTag('list-view-metadata')
  const containerStyles = sx ? merge<BetterSystemStyleObject>(defaultStyles, sx) : defaultStyles
  const announceRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setHasMetadataTitle(!!title)
  })

  useEffect(() => {
    if (announceRef.current) announceFromElement(announceRef.current)
  }, [assistiveAnnouncement])

  const ListViewAnnouncements = () => {
    return (
      <div
        id={`${idPrefix}-list-view-announcements`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        ref={announceRef}
        {...testIdProps('list-view-announcement-container')}
      >
        {assistiveAnnouncement}
      </div>
    )
  }

  const ListFilters = useCallback(() => {
    if (!sectionFilters) return null

    if (Array.isArray(sectionFilters)) {
      return <ListViewSectionFilters links={sectionFilters} />
    } else if (isValidElement(sectionFilters)) {
      return <>{sectionFilters}</>
    }
    return null
  }, [sectionFilters])

  return (
    <Box
      id={`${idPrefix}-list-view-metadata`}
      sx={merge<BetterSystemStyleObject>(
        {gap: anyItemsSelected ? gapFromDensity('spacious') : undefined},
        containerStyles,
      )}
      {...testIdProps('list-view-metadata')}
    >
      <ListViewSelectAllCheckbox
        onToggle={(isSelectAllChecked: boolean) => {
          if (onToggleSelectAll) onToggleSelectAll(isSelectAllChecked)
        }}
      />

      <ListFilters />

      {title && !anyItemsSelected && (
        <Heading as={titleTag} sx={headerStyles} {...testIdProps('list-view-header-title')}>
          {title}
        </Heading>
      )}

      <ListViewActionBar sx={actionsSx} {...actionBarProps}>
        {children}
      </ListViewActionBar>

      {assistiveAnnouncement && <ListViewAnnouncements />}
    </Box>
  )
}

try{ ListViewMetadata.displayName ||= 'ListViewMetadata' } catch {}
try{ ListViewAnnouncements.displayName ||= 'ListViewAnnouncements' } catch {}
try{ ListFilters.displayName ||= 'ListFilters' } catch {}